<template>
  <v-dialog
    v-model="show"
    :contentClass="getContentClasses"
    :fullscreen="false"
    :persistent="persistent"
    :width="dialogWidth"
    :height="dialogHeight"
    :minHeight="minHeight"
    :maxHeight="isMobileSheet ? undefined : maxHeight"
    transition="dialog-bottom-transition"
    elevation="0"
    scrollable
    data-cc="global-modal-dialog"
    @keydown.esc="closeDialog"
    @click:outside="closeDialog"
  >
    <v-card
      elevation="0"
      :data-cc="dialogTestId || undefined"
      :data-status="dialogStatus || undefined"
    >
      <v-card-item v-if="showHeader">
        <slot name="header">
          <v-card-title class="d-flex align-center dialog-header ga-2">
            <slot name="headerInnerPrepend" />
            <span
              v-if="headerText"
              v-sanitize="headerText"
              class="text-h5"
              data-cc="global-modal-dialog-header"
            />

            <v-spacer />

            <slot name="headerInnerAppend" />

            <v-icon-btn
              v-if="closable"
              icon="$close"
              variant="text"
              color="secondary"
              data-cc="global-modal-dialog-header-close"
              @click="closeDialog"
            />
          </v-card-title>
        </slot>
      </v-card-item>

      <div v-if="$slots.appendHeader" class="mx-6 mb-6">
        <slot name="appendHeader" />
      </div>

      <v-divider v-if="showDivider" />
      <v-progress-linear v-if="loading" :height="2" indeterminate />

      <v-card-text
        :class="{
          'pt-0': !showDivider && showHeader,
          'mb-6 pb-0': !showFooter
        }"
      >
        <div class="bmDialog__content">
          <slot />
        </div>
      </v-card-text>

      <slot v-if="showFooter" name="footer">
        <v-divider v-if="isMobileVersion" />

        <v-card-actions
          class="d-flex flex-column-reverse flex-sm-row align-stretch flex-sm-nowrap justify-sm-end pt-4 pt-sm-0 pb-4"
        >
          <slot name="actions">
            <v-btn
              v-if="backButton"
              data-cc="global-modal-dialog-back"
              :text="backLabel || $t('generic.back')"
              variant="text"
              color="secondary"
              @click="emit('back')"
            />
          </slot>

          <v-btn
            v-if="!hideSubmitButton"
            :disabled="disableConfirm"
            :text="submitText || $t('generic.save')"
            variant="flat"
            color="primary"
            data-cc="global-modal-dialog-save"
            @click="confirmDialog"
          />
        </v-card-actions>
      </slot>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';

import { useDisplay } from 'vuetify';

interface Props {
  backButton?: boolean;
  backLabel?: string;
  submitText?: string;
  contentClass?: string;
  persistent?: boolean;
  showDialog?: boolean;
  headerText?: string;
  disableConfirm?: boolean;
  loading?: boolean;
  closable?: boolean;
  hideSubmitButton?: boolean;
  largeDialog?: boolean;
  fullscreen?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  showDivider?: boolean;
  dialogTestId?: string;
  dialogStatus?: string | null;
  width?: string;
  minHeight?: string | number;
  maxHeight?: string | number;
}

const props = withDefaults(defineProps<Props>(), {
  backButton: false,
  backLabel: '',
  submitText: '',
  contentClass: '',
  persistent: true,
  showDialog: false,
  headerText: '',
  disableConfirm: false,
  loading: false,
  closable: true,
  hideSubmitButton: false,
  largeDialog: false,
  fullscreen: true,
  showHeader: true,
  showFooter: true,
  showDivider: true,
  dialogTestId: '',
  dialogStatus: null,
  width: '',
  maxHeight: '80vh'
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'closed'): void;
  (e: 'confirmed'): void;
}>();

const { xs, name } = useDisplay();

const closeDialog = () => {
  if (props.closable) emit('closed');
};

const confirmDialog = () => {
  if (!props.disableConfirm) emit('confirmed');
};

const setFocusOnDialog = () => {
  (document.activeElement as HTMLElement)?.blur();
};

const isMobileVersion = computed(() => xs.value);

const isMobileSheet = computed(() => isMobileVersion.value && props.fullscreen);

const show = computed({
  get: () => props.showDialog,
  set: () => {}
});

const dialogHeight = computed(() => (isMobileSheet.value ? '100%' : undefined));

const dialogWidth = computed(() => {
  if (isMobileSheet.value) return '100%';
  if (props.width) return props.width;
  const sizes: Record<string, number> = {
    md: 70,
    lg: 50,
    xl: 40,
    xxl: 40
  };
  const width = (sizes[name.value] || 50) + (props.largeDialog ? 15 : 0);
  return `${width}vw`;
});

const getContentClasses = computed(() => {
  return [
    'overflow-y-auto',
    props.contentClass,
    isMobileSheet.value ? 'mobile-dialog mobile-dialog--sheet' : ''
  ]
    .filter(Boolean)
    .join(' ');
});

onMounted(() => {
  setFocusOnDialog();
});

watch(
  () => props.showDialog,
  () => {
    setFocusOnDialog();
  }
);
</script>

<style lang="scss" scoped>
.v-dialog--scrollable > .v-overlay__content > .v-card > .v-card-text,
.v-dialog--scrollable > .v-overlay__content > form > .v-card > .v-card-text {
  scrollbar-gutter: stable both-edges;

  .bmDialog__content {
    margin-inline: calc(var(--v-scrollbar-width, 6px) * -1);
  }
}
</style>

<style lang="scss">
.mobile-dialog--sheet {
  margin: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  align-self: flex-end;

  > .v-card {
    border-radius: var(--bm-rounded-2xl) var(--bm-rounded-2xl) 0 0 !important;
  }
}
</style>
